import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import TitleBlock from "@/components/TitleBlock";
import Callout from "@/components/Callout";
import { RefreshCw, Users, Activity, Target, Plug, FileText } from "lucide-react";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend, AreaChart, Area,
} from "recharts";

const WINDOWS = [
  { value: 7,  label: "7d"  },
  { value: 28, label: "28d" },
  { value: 90, label: "90d" },
];

function fmtNum(v) { return (v || 0).toLocaleString(); }
function fmtPct(v) { return `${(v * 100).toFixed(1)}%`; }
function fmtSecs(s) {
  if (!s) return "—";
  const m = Math.floor(s / 60), r = Math.round(s % 60);
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
}

function KPI({ label, value, sub, testid }) {
  return (
    <div className="bg-warm-tint border-t-4 border-cobalt p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] opacity-60">{label}</div>
      <div className="mt-1 text-3xl font-bold text-navy">{value}</div>
      {sub && <div className="text-xs text-midnight opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

export default function SeoTrafficPage() {
  const [days, setDays] = useState(28);
  const [traffic, setTraffic] = useState(null);
  const [landing, setLanding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const [t, l] = await Promise.all([
        api.get(`/seo/organic-traffic?days=${d}`),
        api.get(`/seo/landing-pages?days=${d}&limit=20`),
      ]);
      setTraffic(t.data);
      setLanding(l.data);
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Failed to load traffic." });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onWindow = (d) => { setDays(d); load(d); };

  const sync = async () => {
    setSyncing(true); setMsg(null);
    try {
      const { data } = await api.post("/integrations/google-seo/sync/run-now");
      if (data?.skipped) {
        setMsg({ kind: "err", text: `Skipped: ${data.reason}. Connect Google in Integrations first.` });
      } else if (data?.ok) {
        setMsg({ kind: "ok", text: `Synced ${data.gsc_synced} GSC rows + ${data.ga4_synced} GA4 days.` });
        await load(days);
      } else {
        setMsg({ kind: "err", text: (data?.errors || []).join("; ") || "Sync failed." });
      }
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Sync failed." });
    } finally { setSyncing(false); }
  };

  const connected = traffic?.connected;
  const totals = traffic?.totals || {};
  const daily = traffic?.daily || [];
  const pages = landing?.rows || [];

  return (
    <div data-testid="seo-traffic-page">
      <TitleBlock
        eyebrow="SEO · Phase 2B"
        title="Organic Traffic"
        subtitle="Live GA4 data — what organic search is sending to indielaw.com, which pages convert, and how trends are moving."
        right={
          <button onClick={sync} disabled={syncing || !connected}
                  className="btn-yellow inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="traffic-sync-btn">
            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync now"}
          </button>
        }
      />

      <div className="p-6 md:p-8 space-y-6">
        {msg && (
          <div className="callout text-sm" style={msg.kind === "err" ? { borderTopColor: "#b91c1c" } : {}} data-testid="traffic-msg">
            {msg.text}
          </div>
        )}

        {!connected && !loading && (
          <Callout title="GA4 not connected" testid="traffic-empty">
            Connect Google on the <Link to="/integrations" className="underline text-cobalt font-bold">Integrations</Link> page to pull organic-search sessions, users, and conversions.
            <div className="mt-3">
              <Link to="/integrations" className="btn-yellow inline-flex items-center gap-2" data-testid="traffic-go-integrations">
                <Plug className="w-4 h-4" /> Go to Integrations
              </Link>
            </div>
          </Callout>
        )}

        {/* Window toggle */}
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.22em] opacity-60">Window</span>
          {WINDOWS.map((w) => (
            <button key={w.value} onClick={() => onWindow(w.value)}
                    data-testid={`traffic-window-${w.value}`}
                    className={`text-xs px-3 py-1 border ${days === w.value ? "bg-navy text-warm border-navy" : "bg-warm-tint text-navy border-navy/30 hover:bg-yellow/20"}`}>
              {w.label}
            </button>
          ))}
          {traffic?.window && (
            <span className="ml-auto text-[11px] opacity-60 italic" data-testid="traffic-window-label">
              {traffic.window.start} → {traffic.window.end}
            </span>
          )}
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI label="Organic Sessions"   value={fmtNum(totals.sessions)}
               sub="from Google + other search" testid="kpi-sessions" />
          <KPI label="Unique Users"       value={fmtNum(totals.users)}
               sub="distinct people"            testid="kpi-users" />
          <KPI label="Conversions"        value={fmtNum(Math.round(totals.conversions || 0))}
               sub="GA4-tracked goal completes" testid="kpi-conversions" />
          <KPI label="Engagement Rate"    value={totals.sessions ? fmtPct(totals.engagement_rate || 0) : "—"}
               sub="engaged ÷ all sessions"     testid="kpi-engagement" />
        </div>

        {/* Daily sessions trend */}
        <div className="bg-warm-tint border-t-4 border-cobalt p-6" data-testid="traffic-trend-chart">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-xl font-bold text-navy">Daily Organic Sessions</h2>
            <span className="text-xs uppercase tracking-widest text-cobalt">From GA4</span>
          </div>
          {daily.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={daily}>
                <defs>
                  <linearGradient id="sess_grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="#1D57A5" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#1D57A5" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#141B4D" strokeOpacity={0.1} vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#101820", fontSize: 11 }}
                       tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fill: "#101820", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#141B4D", border: "none", color: "#F2E9DB", padding: "8px 12px" }}
                         itemStyle={{ color: "#F2E9DB" }}
                         labelStyle={{ color: "#FFD100", fontWeight: 700, marginBottom: 4 }} />
                <Legend wrapperStyle={{ fontSize: 12, color: "#101820" }} />
                <Area type="monotone" dataKey="sessions" name="Sessions"
                      stroke="#1D57A5" fill="url(#sess_grad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-sm opacity-60 italic">No data yet — click <strong>Sync now</strong> above.</div>
          )}
        </div>

        {/* Conversions trend */}
        {daily.some((d) => d.conversions > 0) && (
          <div className="bg-warm-tint border-t-4 border-evergreen p-6" data-testid="traffic-conversions-chart">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-xl font-bold text-navy">Daily Conversions (Organic)</h2>
              <span className="text-xs uppercase tracking-widest text-cobalt">GA4 events</span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={daily}>
                <CartesianGrid stroke="#141B4D" strokeOpacity={0.1} vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#101820", fontSize: 11 }}
                       tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fill: "#101820", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#141B4D", border: "none", color: "#F2E9DB", padding: "8px 12px" }}
                         itemStyle={{ color: "#F2E9DB" }}
                         labelStyle={{ color: "#FFD100", fontWeight: 700, marginBottom: 4 }} />
                <Line type="monotone" dataKey="conversions" name="Conversions"
                      stroke="#006747" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Top landing pages */}
        <div className="bg-warm-tint border-t-4 border-yellow p-6" data-testid="landing-pages-table">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-xl font-bold text-navy flex items-center gap-2">
              <FileText className="w-5 h-5" /> Top Organic Landing Pages
            </h2>
            <span className="text-xs uppercase tracking-widest text-cobalt">Sorted by sessions</span>
          </div>
          {pages.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-[0.22em] text-cobalt border-b border-navy/20">
                  <tr>
                    <th className="text-left py-2">Landing Page</th>
                    <th className="text-right py-2"><Users className="w-3 h-3 inline" /> Sessions</th>
                    <th className="text-right py-2">Users</th>
                    <th className="text-right py-2"><Target className="w-3 h-3 inline" /> Conv.</th>
                    <th className="text-right py-2"><Activity className="w-3 h-3 inline" /> Avg Time</th>
                  </tr>
                </thead>
                <tbody>
                  {pages.map((p) => (
                    <tr key={p.landing_page} className="border-b border-warm last:border-0">
                      <td className="py-2 pr-3 text-navy font-medium truncate max-w-[420px]" title={p.landing_page}>
                        {p.landing_page}
                      </td>
                      <td className="py-2 text-right tabular-nums">{fmtNum(p.sessions)}</td>
                      <td className="py-2 text-right tabular-nums">{fmtNum(p.users)}</td>
                      <td className="py-2 text-right tabular-nums">{Math.round(p.conversions)}</td>
                      <td className="py-2 text-right tabular-nums">{fmtSecs(p.avg_session_duration)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-sm opacity-60 italic">No landing-page data yet.</div>
          )}
          {traffic?.last_synced && (
            <div className="mt-3 text-xs opacity-60">Last synced: {new Date(traffic.last_synced).toLocaleString()}</div>
          )}
        </div>

        <Callout title="What counts as Organic Search?">
          GA4&rsquo;s default channel grouping: any session where the source is a search engine (Google, Bing, DuckDuckGo, etc.) and the medium is <code>organic</code> — i.e., not a paid ad. Paid Google Ads traffic shows up under &ldquo;Paid Search&rdquo; instead, so this number is clean of any ad spillover.
        </Callout>
      </div>
    </div>
  );
}
